import {
  COLOR_MODE,
  DataMessage,
  ImageAutoleveler,
  ImageNoiseSuppressor,
  ImageScaler,
} from '@ouster/webviz';
import { ImageSettings } from '@ouster/webviz-react-components';

import { applyRgbAutoExposure } from '../utils/rgbAutoExposure';
import { isWindowColorMode } from './utils/vizColorModePolicyUtils';

export type PerImageProcessors = {
  imageSuppressor: ImageNoiseSuppressor;
  imageAutoLeveler: ImageAutoleveler;
  imageScaler: ImageScaler;
};

type ApplyPerImageTransformsArgs = {
  msg: DataMessage;
  imageSettings: Pick<
    ImageSettings,
    'coloringMode' | 'isAutoLevelsOn' | 'isNoiseSuppressionOn'
  >;
  rgbChannels: string[];
  processors: PerImageProcessors;
  imageIndex: number;
};

export const applyPerImageTransforms = ({
  msg,
  imageSettings,
  rgbChannels,
  processors,
  imageIndex,
}: ApplyPerImageTransformsArgs): DataMessage => {
  const { imageSuppressor, imageAutoLeveler, imageScaler } = processors;
  const { coloringMode, isAutoLevelsOn, isNoiseSuppressionOn } = imageSettings;
  const isRgbMode =
    rgbChannels.length > 0 && rgbChannels.includes(coloringMode);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const rgbApplied: DataMessage = applyRgbAutoExposure(
    msg,
    coloringMode,
    isAutoLevelsOn,
    `2d-image:${imageIndex}:${coloringMode}`
  );
  const rgbPreparedMessage: DataMessage = isRgbMode ? rgbApplied : msg;
  const shouldAutoLevel = isAutoLevelsOn && !isRgbMode;
  const shouldScale =
    !shouldAutoLevel &&
    !isRgbMode &&
    !coloringMode.startsWith(COLOR_MODE.REFLECTIVITY) &&
    !isWindowColorMode(coloringMode);

  imageSuppressor.channelFields = isNoiseSuppressionOn ? [coloringMode] : [];
  imageAutoLeveler.channelFields = shouldAutoLevel ? [coloringMode] : [];
  imageScaler.channelFields = shouldScale ? [coloringMode] : [];

  return imageScaler.mapImage(
    imageAutoLeveler.mapImage(imageSuppressor.mapImage(rgbPreparedMessage))
  );
};
